/*
 * Julvärmen overlays: cookie consent <jv-cookie-banner> (Shopify Customer Privacy API) and the email
 * popup <jv-popup> (present, wheel, advent door).
 * Loaded with defer by sections/jv-cookie-banner.liquid and sections/jv-popup.liquid; the guard keeps it to
 * one run per page. Behaviour lives in custom elements, so it also works after the theme editor re-renders.
 * The banner and the popup take turns: the popup waits while the cookie banner (or any dialog) is open.
 */
(() => {
  'use strict';

  if (window.__jvOverlaysLoaded) return;
  window.__jvOverlaysLoaded = true;

  const JV = (window.JV = window.JV || {});
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mqMobile = window.matchMedia('(max-width: 749px)');
  const mqHover = window.matchMedia('(hover: hover) and (pointer: fine)');
  const DAY = 864e5;
  const PREFS_ID = 'JVCookiePrefs';

  /* ------------------------------------------------------------------ helpers */
  const define = (name, element) => {
    if (!customElements.get(name)) customElements.define(name, element);
  };

  const emit = (name, detail = {}, target = document) =>
    target.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));

  // Shopify adds its captcha token (h-captcha-response) to contact and customer forms through its own hook.
  // A form sent from JavaScript must ask for it with Shopify.captcha.protect first:
  // https://shopify.dev/docs/storefronts/themes/trust-security/captcha
  const captchaReady = (form) =>
    new Promise((resolve) => {
      const captcha = window.Shopify && window.Shopify.captcha;
      if (!captcha || typeof captcha.protect !== 'function') {
        resolve(false);
        return;
      }
      let settled = false;
      const finish = (value) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        resolve(value);
      };
      const timer = window.setTimeout(() => finish(false), 8000);
      try {
        captcha.protect(form, () => finish(true));
      } catch (error) {
        finish(false);
      }
    });

  const storage = {
    area(session) {
      try {
        return session ? window.sessionStorage : window.localStorage;
      } catch (error) {
        return null;
      }
    },
    get(key, session) {
      try {
        const area = storage.area(session);
        return area ? area.getItem(key) : null;
      } catch (error) {
        return null;
      }
    },
    set(key, value, session) {
      try {
        const area = storage.area(session);
        if (area) area.setItem(key, value);
      } catch (error) {
        /* storage unavailable: the choice is simply not remembered */
      }
    },
    remove(key, session) {
      try {
        const area = storage.area(session);
        if (area) area.removeItem(key);
      } catch (error) {
        /* ignore */
      }
    },
  };

  const openDialog = (dialog, opener) => {
    if (!dialog) return;
    if (typeof JV.openDialog === 'function') JV.openDialog(dialog, opener);
    else if (!dialog.open && typeof dialog.showModal === 'function') dialog.showModal();
  };

  const closeDialog = (dialog) => {
    if (!dialog) return;
    if (typeof JV.closeDialog === 'function') JV.closeDialog(dialog);
    else if (dialog.open) dialog.close();
  };

  const toast = (message, options) => {
    if (message && typeof JV.toast === 'function') JV.toast(message, options);
  };

  const setBusy = (button, busy) => {
    if (!button) return;
    if (typeof JV.setBusy === 'function') JV.setBusy(button, busy);
    else button.toggleAttribute('aria-busy', busy);
  };

  const later = (callback, ms) => window.setTimeout(callback, reducedMotion.matches ? 0 : ms);

  const focusMain = () => {
    const main = document.getElementById('MainContent');
    if (main && typeof main.focus === 'function') {
      if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
      main.focus({ preventScroll: true });
    }
  };

  // Custom events for web pixels (Settings > Customer events), which follow the visitor's consent.
  const publish = (name, data) => {
    try {
      const analytics = window.Shopify && window.Shopify.analytics;
      if (analytics && typeof analytics.publish === 'function') analytics.publish(name, data);
    } catch (error) {
      /* analytics are optional */
    }
  };

  /* ------------------------------------------------------------------ overlays take turns */
  const nativeBannerShown = () => {
    const native = document.getElementById('shopify-pc__banner');
    if (!native || !native.getClientRects().length) return false;
    const style = getComputedStyle(native);
    return style.display !== 'none' && style.visibility !== 'hidden';
  };

  const overlays = (JV.overlays = JV.overlays || {});
  overlays.cookie = false;
  overlays.popupShown = false;
  overlays.busy = () =>
    overlays.cookie || nativeBannerShown() || Boolean(document.querySelector('dialog.jv-dialog[open]'));

  /* ------------------------------------------------------------------ "Cookie settings" links */
  // The footer's "Cookie-inställningar" link: <a href="…" data-jv-open="JVCookiePrefs">. With the Julvärmen
  // banner on the page it opens our preferences; otherwise Shopify's own preferences (when Shopify's banner is
  // on); otherwise the link is followed.
  const shopifyCookiePreferences = () => {
    const banner = window.privacyBanner;
    if (banner && typeof banner.showPreferences === 'function') {
      banner.showPreferences();
      return true;
    }
    return false;
  };
  if (typeof JV.openCookiePreferences !== 'function') JV.openCookiePreferences = shopifyCookiePreferences;

  document.addEventListener('click', (event) => {
    const trigger =
      event.target.closest && event.target.closest(`[data-jv-open="${PREFS_ID}"], [data-jv-cookie-settings]`);
    if (!trigger) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const handled = JV.openCookiePreferences(trigger);
    if (handled !== false) event.preventDefault();
  });

  /* ------------------------------------------------------------------ cookie consent */
  let privacyPromise = null;
  const loadPrivacy = () => {
    if (privacyPromise) return privacyPromise;
    privacyPromise = new Promise((resolve, reject) => {
      const api = () => window.Shopify && window.Shopify.customerPrivacy;
      if (api()) {
        resolve(api());
        return;
      }
      const load = () => {
        if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
          reject(new Error('Customer Privacy API is not available'));
          return;
        }
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
          if (error || !api()) reject(error || new Error('Customer Privacy API did not load'));
          else resolve(api());
        });
      };
      if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') load();
      else if (document.readyState === 'complete') load();
      else window.addEventListener('load', load, { once: true });
    });
    privacyPromise.catch(() => {});
    return privacyPromise;
  };

  const CONSENT_TYPES = ['preferences', 'analytics', 'marketing'];

  const visitorConsent = (api) => {
    try {
      return (api && typeof api.currentVisitorConsent === 'function' && api.currentVisitorConsent()) || {};
    } catch (error) {
      return {};
    }
  };

  class JVCookieBanner extends HTMLElement {
    connectedCallback() {
      this.banner = this.querySelector('[data-jv-cookie-banner]');
      this.dialog = this.querySelector('dialog');
      this.toggles = [...this.querySelectorAll('[data-jv-consent-toggle]')];
      this.toastRegion = this.querySelector('[data-jv-cookie-toast-region]');
      this.toastTemplate = this.querySelector('template[data-jv-cookie-toast-template]');
      if (!this.banner || !this.dialog) return;

      this.onClick = this.onClick.bind(this);
      this.addEventListener('click', this.onClick);
      this.onDialogOpen = (event) => {
        if (event.target !== this.dialog) return;
        if (!this.touched) this.applyConsent(visitorConsent(window.Shopify && window.Shopify.customerPrivacy));
        // Start reading at the heading; Tab then reaches the close button and the switches.
        const title = this.dialog.querySelector('.jv-ck__ptitle');
        if (title) title.focus({ preventScroll: true });
      };
      this.dialog.addEventListener('jv:dialog:open', this.onDialogOpen);
      this.openPreferences = this.openPreferences.bind(this);
      JV.openCookiePreferences = this.openPreferences;

      if (designMode) {
        this.setupEditor();
        return;
      }
      this.init();
    }

    disconnectedCallback() {
      if (this.onClick) this.removeEventListener('click', this.onClick);
      if (this.dialog && this.onDialogOpen) this.dialog.removeEventListener('jv:dialog:open', this.onDialogOpen);
      if (JV.openCookiePreferences === this.openPreferences) JV.openCookiePreferences = shopifyCookiePreferences;
      if (this.editorHandlers) {
        Object.entries(this.editorHandlers).forEach(([name, handler]) => document.removeEventListener(name, handler));
      }
      window.clearTimeout(this.hideTimer);
      window.clearTimeout(this.toastTimer);
      if (this.banner && !this.banner.hidden) overlays.cookie = false;
    }

    async init() {
      let api;
      try {
        api = await loadPrivacy();
      } catch (error) {
        return;
      }
      if (!this.isConnected) return;
      const consent = visitorConsent(api);
      const decided = CONSENT_TYPES.some((type) => consent[type] === 'yes' || consent[type] === 'no');
      if (decided) return;
      let needed = this.dataset.showTo === 'everyone';
      if (!needed) {
        try {
          needed = typeof api.shouldShowBanner === 'function' && Boolean(api.shouldShowBanner());
        } catch (error) {
          needed = false;
        }
      }
      if (!needed || nativeBannerShown()) return;
      this.showBanner();
      this.watchNativeBanner();
    }

    // Shopify's own banner is added by Shopify's script, sometimes after ours: if it turns up, ours goes.
    watchNativeBanner() {
      if (typeof MutationObserver !== 'function') return;
      const observer = new MutationObserver(() => {
        if (!nativeBannerShown()) return;
        stop();
        this.hideBanner(true);
      });
      const timer = window.setTimeout(() => stop(), 15000);
      const stop = () => {
        observer.disconnect();
        window.clearTimeout(timer);
      };
      observer.observe(document.body, { childList: true });
    }

    onClick(event) {
      const toastClose = event.target.closest('[data-jv-cookie-toast-close]');
      if (toastClose) {
        event.preventDefault();
        this.hideToast(true);
        return;
      }
      const toggle = event.target.closest('[data-jv-consent-toggle]');
      if (toggle) {
        event.preventDefault();
        const on = toggle.getAttribute('aria-checked') !== 'true';
        this.touched = true;
        const type = toggle.getAttribute('data-jv-consent-toggle');
        this.toggles.forEach((other) => {
          if (other.getAttribute('data-jv-consent-toggle') === type) this.setToggle(other, on);
        });
        return;
      }
      const action = event.target.closest('[data-jv-consent]');
      if (!action) return;
      event.preventDefault();
      switch (action.getAttribute('data-jv-consent')) {
        case 'preferences':
          this.openPreferences(action);
          break;
        case 'accept':
          // In the preferences window the switches flip on first, so the visitor sees what is accepted.
          if (this.dialog.open && !reducedMotion.matches) {
            this.toggles.forEach((toggle) => this.setToggle(toggle, true));
            window.setTimeout(() => this.save({ preferences: true, analytics: true, marketing: true }, action), 260);
          } else {
            this.save({ preferences: true, analytics: true, marketing: true }, action);
          }
          break;
        case 'reject':
          this.save({ preferences: false, analytics: false, marketing: false }, action);
          break;
        case 'save':
          this.save(this.readToggles(), action);
          break;
        default:
          break;
      }
    }

    setToggle(toggle, on) {
      toggle.setAttribute('aria-checked', String(on));
      const ctrl = toggle.closest('.jv-ck__cat-ctrl');
      if (ctrl) ctrl.classList.toggle('is-on', on);
    }

    readToggles() {
      const choice = { preferences: false, analytics: false, marketing: false };
      this.toggles.forEach((toggle) => {
        if (toggle.getAttribute('aria-checked') === 'true') choice[toggle.getAttribute('data-jv-consent-toggle')] = true;
      });
      return choice;
    }

    // Optional categories only show as on when the visitor switched them on before.
    applyConsent(consent) {
      this.toggles.forEach((toggle) => {
        this.setToggle(toggle, consent[toggle.getAttribute('data-jv-consent-toggle')] === 'yes');
      });
    }

    openPreferences(opener) {
      this.touched = false;
      const api = window.Shopify && window.Shopify.customerPrivacy;
      this.applyConsent(visitorConsent(api));
      if (!api && !designMode) {
        loadPrivacy()
          .then((loaded) => {
            if (!this.touched) this.applyConsent(visitorConsent(loaded));
          })
          .catch(() => {});
      }
      openDialog(this.dialog, opener && opener.nodeType === 1 ? opener : undefined);
      return true;
    }

    async save(choice, button) {
      if (this.saving) return;
      if (designMode) {
        this.hideBanner();
        closeDialog(this.dialog);
        return;
      }
      const consent = {
        analytics: Boolean(choice.analytics),
        marketing: Boolean(choice.marketing),
        preferences: Boolean(choice.preferences),
        sale_of_data: Boolean(choice.marketing),
      };
      this.saving = true;
      setBusy(button, true);
      try {
        const api = await loadPrivacy();
        await new Promise((resolve, reject) => {
          const timeout = window.setTimeout(() => reject(new Error('Consent was not saved in time')), 8000);
          try {
            api.setTrackingConsent(consent, (result) => {
              window.clearTimeout(timeout);
              if (result && result.error) reject(new Error(String(result.error)));
              else resolve();
            });
          } catch (error) {
            window.clearTimeout(timeout);
            reject(error);
          }
        });
        setBusy(button, false);
        this.hideBanner();
        if (this.dialog.open) {
          // Focus goes back to whatever opened the window; when that was the banner (now hiding), move it
          // to the main content instead of letting it fall to the page body.
          this.dialog.addEventListener(
            'jv:dialog:close',
            () => {
              const active = document.activeElement;
              if (!active || active === document.body || this.banner.contains(active)) focusMain();
            },
            { once: true }
          );
          closeDialog(this.dialog);
        }
        later(() => this.showToast(), 340);
        emit('jv:consent:change', consent);
      } catch (error) {
        setBusy(button, false);
        toast(this.dataset.error, { tone: 'error' });
      } finally {
        this.saving = false;
      }
    }

    showBanner() {
      if (!this.banner.hidden && this.banner.classList.contains('is-in')) return;
      window.clearTimeout(this.hideTimer);
      overlays.cookie = true;
      this.banner.classList.remove('is-out');
      this.banner.hidden = false;
      this.banner.classList.add('is-in');
      emit('jv:cookie-banner:open', {}, this);
    }

    hideBanner(instant) {
      if (this.banner.hidden) return;
      const hadFocus = this.banner.contains(document.activeElement);
      this.banner.classList.remove('is-in');
      this.banner.classList.add('is-out');
      overlays.cookie = false;
      const done = () => {
        this.banner.hidden = true;
        this.banner.classList.remove('is-out');
      };
      if (instant || reducedMotion.matches) done();
      else this.hideTimer = window.setTimeout(done, 330);
      if (hadFocus) focusMain();
      emit('jv:cookie-banner:close', {}, this);
    }

    // "Sparat. Du kan ändra dig i sidfoten." bottom left for about six seconds, as on the board.
    showToast() {
      if (!this.toastRegion || !this.toastTemplate) return;
      window.clearTimeout(this.toastTimer);
      this.toastRegion.replaceChildren(this.toastTemplate.content.cloneNode(true));
      this.toastTimer = window.setTimeout(() => this.hideToast(), 6060);
    }

    hideToast(instant) {
      if (!this.toastRegion) return;
      window.clearTimeout(this.toastTimer);
      const element = this.toastRegion.querySelector('.jv-ck__toast');
      if (!element) return;
      const hadFocus = element.contains(document.activeElement);
      const done = () => {
        this.toastRegion.replaceChildren();
        if (hadFocus) focusMain();
      };
      if (instant || reducedMotion.matches) {
        done();
        return;
      }
      element.classList.add('is-out');
      this.toastTimer = window.setTimeout(done, 340);
    }

    // Theme editor: selecting the section shows the banner, selecting a category opens the window.
    setupEditor() {
      const host = this.closest('.shopify-section');
      const id = host ? host.id.replace('shopify-section-', '') : '';
      const mine = (event) => event.detail && (event.detail.sectionId === id || event.detail.sectionId === this.dataset.sectionId);
      this.editorHandlers = {
        'shopify:section:select': (event) => mine(event) && this.showBanner(),
        'shopify:section:load': (event) => mine(event) && this.showBanner(),
        'shopify:section:deselect': (event) => {
          if (!mine(event)) return;
          this.hideBanner(true);
          closeDialog(this.dialog);
        },
        'shopify:block:select': (event) => mine(event) && this.openPreferences(),
      };
      Object.entries(this.editorHandlers).forEach(([name, handler]) => document.addEventListener(name, handler));
    }
  }
  define('jv-cookie-banner', JVCookieBanner);

  /* ------------------------------------------------------------------ email popup */
  const POPUP_DISMISSED = 'jv-popup-dismissed';
  const POPUP_SUBSCRIBED = 'jv-popup-subscribed';
  const POPUP_SUBMITTED = 'jv-popup-submitted';
  const WHEEL_PRIZE = 'jv-wheel-prize';
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  // A fair draw: every index from 0 to count - 1 equally likely (rejection sampling, no modulo bias).
  const randomIndex = (count) => {
    try {
      if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
        const limit = Math.floor(0x100000000 / count) * count;
        const value = new Uint32Array(1);
        do {
          window.crypto.getRandomValues(value);
        } while (value[0] >= limit);
        return value[0] % count;
      }
    } catch (error) {
      /* fall back to Math.random */
    }
    return Math.floor(Math.random() * count);
  };

  // Read once per page load: the page right after a normal (non-background) submit shows the result.
  const popupSubmitted = storage.get(POPUP_SUBMITTED, true) || '';
  storage.remove(POPUP_SUBMITTED, true);

  class JVPopup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;
      this.form = this.querySelector('form');
      this.email = this.querySelector('[data-jv-pop-email]');
      this.error = this.querySelector('[data-jv-pop-error]');
      this.submit = this.querySelector('[data-jv-pop-submit]');
      this.submitLabel = this.querySelector('[data-jv-pop-submit-label]');
      this.stage = this.querySelector('[data-jv-pop-stage]');
      this.status = this.querySelector('[data-jv-pop-status]');
      this.wheel = this.querySelector('[data-jv-wheel]');
      this.pointer = this.querySelector('.jv-wh__ptr');
      this.highlight = this.querySelector('[data-jv-wheel-hl]');
      this.tagsInput = this.querySelector('input[name="contact[tags]"]');
      if (this.tagsInput && !this.tagsInput.hasAttribute('data-base')) this.tagsInput.setAttribute('data-base', this.tagsInput.value);
      this.variant = this.dataset.variant || 'present';
      this.segments = this.readSegments();
      this.prize = -1;
      this.done = false;
      if (this.submitLabel) this.submitLabel.dataset.label = this.submitLabel.textContent;

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      if (this.form) this.form.addEventListener('submit', (event) => this.onSubmit(event), { signal });
      if (this.email) this.email.addEventListener('input', () => this.clearError(), { signal });
      this.dialog.addEventListener(
        'jv:dialog:close',
        () => {
          if (!this.done && !designMode) storage.set(POPUP_DISMISSED, String(Date.now()));
          this.clearTimers();
          if (designMode) window.setTimeout(() => this.resetPreview(), 0);
        },
        { signal }
      );

      if (designMode) return;
      this.boot();
    }

    disconnectedCallback() {
      this.clearTriggers();
      this.clearTimers();
      if (this.controller) this.controller.abort();
    }

    boot() {
      const params = new URLSearchParams(window.location.search);
      const posted = params.get('customer_posted') === 'true';
      const formId = this.dataset.formId;
      const mine = popupSubmitted === formId || window.location.hash === `#${formId}`;
      const success = this.dataset.posted === 'true';
      const errors = this.dataset.errors === 'true';

      // A signup through any customer form on the site counts as subscribed.
      if (posted && success) storage.set(POPUP_SUBSCRIBED, String(Date.now()));

      if (mine && success) {
        this.cleanUrl();
        this.open(true);
        this.reveal();
        return;
      }
      if (mine && errors) {
        this.open(true);
        window.setTimeout(() => this.email && this.email.focus(), 60);
        return;
      }
      if (errors && !mine) this.clearError();

      if (this.dataset.enabled !== 'true' || this.dataset.skip === 'true') return;
      if (storage.get(POPUP_SUBSCRIBED)) return;
      const dismissed = Number(storage.get(POPUP_DISMISSED) || 0);
      const days = Math.max(1, Number(this.dataset.days) || 14);
      if (dismissed && Date.now() - dismissed < days * DAY) return;
      if (mqMobile.matches && this.dataset.mobile !== 'true') return;
      this.schedule();
    }

    schedule() {
      const delay = Math.max(0, Number(this.dataset.delay) || 0) * 1000;
      this.preloadTimer = window.setTimeout(() => this.preload(), Math.max(0, delay - 2500));
      this.timer = window.setTimeout(() => this.open(), delay);
      if (this.dataset.exit === 'true' && mqHover.matches) {
        const armedAt = Date.now() + 3000;
        this.onMouseOut = (event) => {
          if (event.relatedTarget || event.clientY > 0 || Date.now() < armedAt) return;
          this.open();
        };
        document.addEventListener('mouseout', this.onMouseOut);
      }
    }

    clearTriggers() {
      window.clearTimeout(this.timer);
      window.clearTimeout(this.preloadTimer);
      window.clearTimeout(this.retryTimer);
      if (this.onMouseOut) document.removeEventListener('mouseout', this.onMouseOut);
      this.onMouseOut = null;
    }

    clearTimers() {
      (this.timers || []).forEach((timer) => window.clearTimeout(timer));
      this.timers = [];
    }

    later(callback, ms) {
      this.timers = this.timers || [];
      this.timers.push(window.setTimeout(callback, ms));
    }

    preload() {
      this.querySelectorAll('img[loading="lazy"]').forEach((image) => {
        image.loading = 'eager';
      });
    }

    open(force) {
      if (this.opened || !this.dialog || !this.isConnected) return;
      if (!force && (overlays.busy() || overlays.popupShown)) {
        if (overlays.popupShown) return;
        window.clearTimeout(this.retryTimer);
        this.retryTimer = window.setTimeout(() => this.open(), 2500);
        return;
      }
      this.opened = true;
      overlays.popupShown = true;
      this.clearTriggers();
      this.preload();
      openDialog(this.dialog);
      // Screen readers start at the heading instead of the first button in the picture.
      const title = this.querySelector('[data-view]:not([hidden]) .jv-pop__title');
      if (title) title.focus({ preventScroll: true });
      if (!force) publish('jv_popup_view', { popup: this.variant });
    }

    /* ---------------------------------------------------------------- the wheel's draw */
    // The segments drawn on the wheel: [{ n: setting number, label, tag }]. Only segments with a code are there.
    readSegments() {
      const script = this.querySelector('script[data-jv-wheel-segments]');
      if (!script) return [];
      try {
        const list = JSON.parse(script.textContent || '[]');
        return Array.isArray(list) ? list.filter((item) => item && Number.isFinite(item.n) && typeof item.label === 'string') : [];
      } catch (error) {
        return [];
      }
    }

    // Chosen before the signup is sent and kept in localStorage, so a reload (or the normal form post after a
    // captcha) shows the same prize. Every segment on the wheel is equally likely.
    pickPrize() {
      const segments = this.segments || [];
      if (!segments.length) return -1;
      const key = `${WHEEL_PRIZE}-${this.dataset.sectionId}`;
      if (!designMode) {
        let kept = null;
        try {
          kept = JSON.parse(storage.get(key) || 'null');
        } catch (error) {
          kept = null;
        }
        const index = kept ? segments.findIndex((item) => item.n === kept.n && item.label === kept.label) : -1;
        if (index > -1) return index;
      }
      const index = randomIndex(segments.length);
      if (!designMode) {
        storage.set(key, JSON.stringify({ n: segments[index].n, label: segments[index].label, at: Date.now() }));
      }
      return index;
    }

    // The prize goes on the customer as a tag too, e.g. "newsletter, popup, hjulet-15".
    applyTag(index) {
      const segment = this.segments[index];
      if (!this.tagsInput || !segment) return;
      const base = this.tagsInput.getAttribute('data-base') || '';
      this.tagsInput.value = [base, segment.tag].filter(Boolean).join(', ');
    }

    showWin(index) {
      const segment = this.segments[index];
      if (!segment) return;
      const label = segment.label.replace(/ %/g, '\u00a0%');
      const title = this.querySelector('[data-jv-wheel-title]');
      if (title && this.dataset.won) title.textContent = this.dataset.won.replace('[prize]', label);
      // The codes are only in the page after Shopify confirmed the signup (or in the theme editor).
      let codes = {};
      const script = this.querySelector('script[data-jv-wheel-codes]');
      try {
        codes = JSON.parse(script ? script.textContent : '{}') || {};
      } catch (error) {
        codes = {};
      }
      const code = typeof codes[String(segment.n)] === 'string' ? codes[String(segment.n)] : '';
      const codeText = this.querySelector('[data-jv-wheel-code]');
      if (codeText) codeText.textContent = code;
      const copy = this.querySelector('.jv-pop__codebox [data-jv-copy]');
      if (copy) copy.setAttribute('data-jv-copy', code);
      const box = this.querySelector('.jv-pop__codebox');
      if (box) box.style.display = code ? '' : 'none';
      if (this.status && this.dataset.result) this.status.textContent = this.dataset.result.replace('[prize]', label);
    }

    /* ---------------------------------------------------------------- signup */
    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = false;
      if (this.email) this.email.setAttribute('aria-invalid', 'true');
    }

    clearError() {
      if (this.error) {
        this.error.hidden = true;
        this.error.textContent = '';
      }
      if (this.email) this.email.removeAttribute('aria-invalid');
    }

    setSubmitting(busy) {
      if (!this.submit) return;
      if (this.variant === 'wheel') {
        // The wheel button keeps its words and says "Hjulet snurrar" while it spins.
        this.submit.setAttribute('aria-disabled', String(busy));
        this.submit.toggleAttribute('aria-busy', busy && !this.spinning);
        return;
      }
      setBusy(this.submit, busy);
    }

    async onSubmit(event) {
      // A normal submission we started ourselves: let Shopify's captcha hook take it from here.
      if (this.native) return;
      if (this.sending || this.done) {
        event.preventDefault();
        return;
      }
      const value = this.email ? this.email.value.trim() : '';
      if (!value || !EMAIL_PATTERN.test(value)) {
        event.preventDefault();
        this.showError(this.dataset.invalid || '');
        if (this.email) this.email.focus();
        return;
      }
      const consent = this.querySelector('[data-jv-pop-consent]');
      if (consent && !consent.checked) {
        event.preventDefault();
        consent.focus();
        if (typeof consent.reportValidity === 'function') consent.reportValidity();
        return;
      }
      if (this.email) this.email.value = value;
      this.clearError();

      // The wheel draws its prize now, before anything is sent, and tags the customer with it.
      if (this.variant === 'wheel') {
        this.prize = this.pickPrize();
        this.applyTag(this.prize);
      }

      // Theme editor: play the animation without creating a customer.
      if (designMode) {
        event.preventDefault();
        this.reveal();
        return;
      }

      // Without fetch the form posts normally and the next page shows the result.
      if (typeof window.fetch !== 'function' || typeof FormData !== 'function') {
        storage.set(POPUP_SUBMITTED, this.dataset.formId, true);
        return;
      }
      event.preventDefault();
      this.sending = true;
      this.setSubmitting(true);
      publish('jv_popup_signup', { popup: this.variant });
      try {
        // Shopify only accepts this form with its captcha token (h-captcha-response). A form sent from
        // JavaScript has to ask for it first; without a token, send the form the normal way instead.
        const ready = await captchaReady(this.form);
        const token = this.form.querySelector('[name="h-captcha-response"]');
        if (!ready || !token || !token.value) {
          this.nativeSubmit();
          return;
        }
        const response = await fetch(this.form.action, {
          method: 'POST',
          body: new FormData(this.form),
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
        });
        const url = response.url || '';
        if (/\/challenge/.test(url) || response.status === 400) {
          // Shopify wants a captcha first: send the form the normal way so the visitor can answer it.
          this.nativeSubmit();
          return;
        }
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector(`jv-popup[data-form-id="${CSS.escape(this.dataset.formId)}"]`);
        const success = /customer_posted=true/.test(url) || (fresh && fresh.getAttribute('data-posted') === 'true');
        const failed = fresh && fresh.getAttribute('data-errors') === 'true';
        if (success && !failed) {
          this.fillSlots(fresh);
          storage.set(POPUP_SUBSCRIBED, String(Date.now()));
          this.sending = false;
          this.reveal();
          return;
        }
        if (failed || response.ok) {
          this.sending = false;
          this.setSubmitting(false);
          this.showError(this.dataset.error || '');
          if (this.email) this.email.focus();
          return;
        }
        this.nativeSubmit();
      } catch (error) {
        this.nativeSubmit();
      }
    }

    // Sends the form the normal way through Shopify's captcha step. HTMLFormElement.submit() would skip that
    // step and Shopify answers "Missing CAPTCHA token", so this fires a real submit event (requestSubmit) or,
    // in old browsers, submits from Shopify.captcha.protect's callback as Shopify documents.
    nativeSubmit() {
      storage.set(POPUP_SUBMITTED, this.dataset.formId, true);
      this.sending = false;
      this.native = true;
      if (typeof this.form.requestSubmit === 'function') {
        try {
          this.form.requestSubmit(this.submit && this.submit.form === this.form ? this.submit : undefined);
          return;
        } catch (error) {
          /* fall through */
        }
      }
      captchaReady(this.form).then(() => HTMLFormElement.prototype.submit.call(this.form));
    }

    // The code, its copy button and "valid until" come from Shopify's answer, so they are never in the
    // page before a signup went through.
    fillSlots(fresh) {
      if (!fresh) return;
      const current = [...this.querySelectorAll('[data-jv-pop-slot]')];
      const incoming = [...fresh.querySelectorAll('[data-jv-pop-slot]')];
      current.forEach((slot, index) => {
        const source = incoming[index];
        if (source && source.innerHTML.trim()) slot.innerHTML = source.innerHTML;
      });
    }

    /* ---------------------------------------------------------------- the animations */
    reveal() {
      this.done = true;
      this.clearTriggers();
      // The code card in the stage is inert (not focusable, so it can't scroll into view) until now.
      this.querySelectorAll('[data-jv-pop-stage-slot]').forEach((slot) => slot.removeAttribute('inert'));
      if (this.variant === 'wheel') {
        this.spin();
        return;
      }
      if (this.stage) this.stage.classList.add('is-open');
      this.showDone();
    }

    // Board logic with n equal slices: slice i is centred at i*(360/n)deg; turning the wheel clockwise by R puts
    // it at i*(360/n) + R, so the pointer (0deg) needs R = 360*k - i*(360/n). Six full turns, 4.5 s,
    // cubic-bezier(.12,.67,.12,1), ending on the slice that was drawn.
    spin() {
      const index = this.prize > -1 ? this.prize : this.pickPrize();
      if (!this.wheel || index < 0) {
        this.showDone();
        return;
      }
      this.prize = index;
      const step = 360 / this.segments.length;
      const target = 360 * 6 - index * step;
      if (this.highlight) this.highlight.setAttribute('transform', `rotate(${index * step})`);
      const seconds = reducedMotion.matches ? 0.8 : 4.5;
      this.spinning = true;
      if (this.submitLabel && this.dataset.spinning) this.submitLabel.textContent = this.dataset.spinning;
      this.setSubmitting(true);
      this.wheel.style.setProperty('--jv-wh-dur', `${seconds}s`);
      // Start from 0 in a frame of its own so the transition always runs.
      this.wheel.classList.remove('is-spinning', 'is-won');
      this.wheel.style.setProperty('--jv-wh-deg', '0deg');
      void this.wheel.offsetWidth;
      requestAnimationFrame(() => {
        this.wheel.classList.add('is-spinning');
        this.wheel.style.setProperty('--jv-wh-deg', `${target}deg`);
        if (this.pointer && !reducedMotion.matches) {
          this.pointer.classList.remove('is-ticking');
          void this.pointer.getBoundingClientRect();
          this.pointer.classList.add('is-ticking');
        }
      });
      this.later(() => {
        this.spinning = false;
        this.wheel.classList.add('is-won');
        if (this.pointer) this.pointer.classList.remove('is-ticking');
        this.showWin(index);
        this.showDone();
      }, seconds * 1000 + 250);
    }

    showDone() {
      const form = this.querySelector('[data-view="form"]');
      const done = this.querySelector('[data-view="done"]');
      if (!done) return;
      if (form) form.hidden = true;
      done.hidden = false;
      done.classList.add('is-shown');
      const title = done.querySelector('.jv-pop__title');
      if (title && title.id) this.dialog.setAttribute('aria-labelledby', title.id);
      if (title) window.setTimeout(() => title.focus({ preventScroll: true }), 30);
    }

    // Theme editor only: back to the signup view after the preview was closed.
    resetPreview() {
      this.done = false;
      this.spinning = false;
      this.prize = -1;
      this.clearTimers();
      if (this.tagsInput) this.tagsInput.value = this.tagsInput.getAttribute('data-base') || this.tagsInput.value;
      if (this.stage) this.stage.classList.remove('is-open');
      this.querySelectorAll('[data-jv-pop-stage-slot]').forEach((slot) => slot.setAttribute('inert', ''));
      if (this.wheel) {
        this.wheel.classList.remove('is-spinning', 'is-won');
        this.wheel.style.setProperty('--jv-wh-deg', '0deg');
      }
      if (this.pointer) this.pointer.classList.remove('is-ticking');
      if (this.submit) {
        this.submit.removeAttribute('aria-disabled');
        this.submit.removeAttribute('aria-busy');
        this.submit.disabled = false;
      }
      if (this.submitLabel && this.submitLabel.dataset.label) this.submitLabel.textContent = this.submitLabel.dataset.label;
      const form = this.querySelector('[data-view="form"]');
      const done = this.querySelector('[data-view="done"]');
      if (form) form.hidden = false;
      if (done) {
        done.hidden = true;
        done.classList.remove('is-shown');
      }
      const title = form && form.querySelector('.jv-pop__title');
      if (title && title.id) this.dialog.setAttribute('aria-labelledby', title.id);
    }

    cleanUrl() {
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('customer_posted');
        url.hash = '';
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (error) {
        /* leave the address as it is */
      }
    }
  }
  define('jv-popup', JVPopup);
})();
